import React, { useState, useEffect } from 'react';
import STAGE_IMPORT_CONFIG from '../../../config/stageImportConfig';
import { validateGpxFile, getGpxPreview } from '../../../utils/gpxParser';

const StageImportForm = ({ onSubmit, loading, roads = [] }) => {
    const [formData, setFormData] = useState({
        stageName: '',
        section: '',
        kind: 'OS',
        stageTyp: '',
        runNo: 1,
        resultStageId: '',
        gpxMode: 'upload',
        useRoadId: 0,
        gpxFile: null,
        thinStep: STAGE_IMPORT_CONFIG.defaultThinStep,
        storeXml: false,
        stageType: 'stage'
    });

    const [filePreview, setFilePreview] = useState(null);
    const [fileError, setFileError] = useState('');

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        setFileError('');
        setFilePreview(null);

        if (!file) {
            setFormData(prev => ({ ...prev, gpxFile: null }));
            return;
        }

        const validation = validateGpxFile(file);
        if (!validation.valid) {
            setFileError(validation.error);
            setFormData(prev => ({ ...prev, gpxFile: null }));
            return;
        }

        setFormData(prev => ({ ...prev, gpxFile: file }));

        // Get preview
        const preview = await getGpxPreview(file);
        if (preview.error) {
            setFileError(preview.error);
        } else {
            setFilePreview(preview);
        }
    };

    const handleGpxModeChange = (mode) => {
        setFormData(prev => ({ ...prev, gpxMode: mode }));
        setFileError('');
        setFilePreview(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!formData.stageName.trim()) {
            alert('Stage name is required');
            return;
        }

        if (formData.stageType === 'stage' && !formData.section) {
            alert('Please select a section for Stage');
            return;
        }

        if (formData.gpxMode === 'upload' && !formData.gpxFile) {
            alert('Please select a GPX file');
            return;
        }

        if (formData.gpxMode === 'use_road' && !formData.useRoadId) {
            alert('Please select an existing road');
            return;
        }

        onSubmit(formData);
    };

    const handleReset = () => {
        setFormData({
            stageName: '',
            section: '',
            kind: 'OS',
            stageTyp: '',
            runNo: 1,
            resultStageId: '',
            gpxMode: 'upload',
            useRoadId: 0,
            gpxFile: null,
            thinStep: STAGE_IMPORT_CONFIG.defaultThinStep,
            storeXml: false,
            stageType: 'stage'
        });
        setFileError('');
        setFilePreview(null);
    };

    return (
        <form onSubmit={handleSubmit} className="stage-import-form">
            {/* Import Type Selection */}
            <div className="row">
                <div className="col">
                    <label>Typ importu (Gdzie zapisać?)</label>
                    <div className="gpx-mode-selector" style={{ marginBottom: '15px' }}>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="stageType"
                                value="stage"
                                checked={formData.stageType === 'stage'}
                                onChange={() => setFormData(prev => ({ ...prev, stageType: 'stage' }))}
                            />
                            <span>Stage (Tabela 'stages')</span>
                        </label>
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="stageType"
                                value="road"
                                checked={formData.stageType === 'road'}
                                onChange={() => setFormData(prev => ({ ...prev, stageType: 'road', gpxMode: 'upload' }))}
                            />
                            <span>Road (Tabela 'roads' - tylko biblioteka śladów)</span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Basic Information */}
            <div className="row">
                <div className="col">
                    <label htmlFor="stageName">
                        Nazwa <span className="required">*</span>
                    </label>
                    <input
                        type="text"
                        id="stageName"
                        name="stageName"
                        value={formData.stageName}
                        onChange={handleInputChange}
                        placeholder="np. Chmielnik 1 / Dojazd A→B"
                        required
                    />
                </div>

                {formData.stageType === 'stage' && (
                    <>
                        <div className="col">
                            <label htmlFor="section">
                                Section <span className="required">*</span>
                            </label>
                            <select
                                id="section"
                                name="section"
                                value={formData.section}
                                onChange={handleInputChange}
                                required={formData.stageType === 'stage'}
                            >
                                <option value="">— wybierz —</option>
                                {STAGE_IMPORT_CONFIG.allowedSections.map(section => (
                                    <option key={section} value={section}>{section}</option>
                                ))}
                            </select>
                            <div className="help">Np. LEG 1, LEG 2…</div>
                        </div>
                        <div className="col">
                            <label htmlFor="kind">Rodzaj</label>
                            <select
                                id="kind"
                                name="kind"
                                value={formData.kind}
                                onChange={handleInputChange}
                            >
                                <option value="OS">Odcinek (OS)</option>
                                <option value="LIAISON">Dojazdówka (LIAISON)</option>
                            </select>
                        </div>
                    </>
                )}
            </div>

            {/* Type and Run Information - Only for stages */}
            {formData.stageType === 'stage' && (
                <div className="row">
                    <div className="col">
                        <label htmlFor="stageTyp">Typ (bazowy odcinek, opcjonalnie)</label>
                        <input
                            type="text"
                            id="stageTyp"
                            name="stageTyp"
                            value={formData.stageTyp}
                            onChange={handleInputChange}
                            placeholder="Lubenia / Chmielnik"
                        />
                        <div className="help">Dla OS może wskazywać wspólny GPX (biblioteka "roads").</div>
                    </div>
                    <div className="col">
                        <label htmlFor="runNo">Sekwencja (run)</label>
                        <select
                            id="runNo"
                            name="runNo"
                            value={formData.runNo}
                            onChange={handleInputChange}
                        >
                            {STAGE_IMPORT_CONFIG.runNumbers.map(num => (
                                <option key={num} value={num}>{num}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col">
                        <label htmlFor="resultStageId">ResultStage ID (INT)</label>
                        <input
                            type="number"
                            id="resultStageId"
                            name="resultStageId"
                            value={formData.resultStageId}
                            onChange={handleInputChange}
                            min="0"
                            step="1"
                            placeholder="np. 101"
                        />
                    </div>
                </div>
            )}

            {/* GPX Source */}
            <div className="row">
                <div className="col">
                    <label>Źródło GPX</label>
                    <div className="gpx-mode-selector">
                        <label className="radio-label">
                            <input
                                type="radio"
                                name="gpxMode"
                                value="upload"
                                checked={formData.gpxMode === 'upload'}
                                onChange={() => handleGpxModeChange('upload')}
                            />
                            <span>Wgraj plik GPX</span>
                        </label>
                        {formData.stageType === 'stage' && (
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    name="gpxMode"
                                    value="use_road"
                                    checked={formData.gpxMode === 'use_road'}
                                    onChange={() => handleGpxModeChange('use_road')}
                                />
                                <span>Użyj istniejącego GPX (Road)</span>
                            </label>
                        )}
                    </div>

                    {formData.gpxMode === 'upload' && (
                        <div className="gpx-upload-section">
                            <label htmlFor="gpxFile">GPX file</label>
                            <input
                                type="file"
                                id="gpxFile"
                                name="gpxFile"
                                onChange={handleFileChange}
                                accept=".gpx,application/gpx+xml,application/xml,text/xml"
                            />
                            {filePreview && (
                                <div className="file-preview success">
                                    ✓ {filePreview.pointCount} points found
                                </div>
                            )}
                            {fileError && (
                                <div className="file-preview error">
                                    ✗ {fileError}
                                </div>
                            )}
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="storeXml"
                                    checked={formData.storeXml}
                                    onChange={handleInputChange}
                                />
                                <span>Zachowaj raw GPX XML w DB</span>
                            </label>
                        </div>
                    )}

                    {formData.gpxMode === 'use_road' && (
                        <div className="gpx-road-section">
                            <label htmlFor="useRoadId">Wybierz istniejący GPX (Road)</label>
                            <select
                                id="useRoadId"
                                name="useRoadId"
                                value={formData.useRoadId}
                                onChange={handleInputChange}
                            >
                                <option value="0">— wybierz —</option>
                                {roads.map(road => (
                                    <option key={road.id} value={road.id}>
                                        {road.name} (ID {road.id}, {road.distance_km} km)
                                    </option>
                                ))}
                            </select>
                            <div className="help">Lista pochodzi z tabeli roads.</div>
                        </div>
                    )}
                </div>

                <div className="col">
                    <label htmlFor="thinStep">Thin points (every Nth)</label>
                    <input
                        type="number"
                        id="thinStep"
                        name="thinStep"
                        value={formData.thinStep}
                        onChange={handleInputChange}
                        min="1"
                        step="1"
                    />
                    <div className="help">1 = keep all points</div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="buttons">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
                    Reset
                </button>
            </div>
        </form>
    );
};

export default StageImportForm;
